/**
 * Typed errors for cross-app use.
 * Feature code throws these instead of stringly-typed `new Error('...')`.
 * UI catches and renders i18n message via the error code.
 */

export type KilatErrorCode =
  | 'NOT_SIGNED_IN'
  | 'INSUFFICIENT_PERMISSION'
  | 'TENANT_NOT_FOUND'
  | 'OUTLET_NOT_FOUND'
  | 'TICKET_NOT_FOUND'
  | 'INVALID_TRANSITION'
  | 'PACKAGE_NOT_FOUND'
  | 'VEHICLE_NOT_FOUND'
  | 'AGENT_CODE_INVALID'
  | 'AGENT_NOT_ACTIVE'
  | 'INVOICE_NOT_FOUND'
  | 'INVOICE_ALREADY_PAID'
  | 'VOUCHER_INVALID'
  | 'VOUCHER_EXPIRED'
  | 'VOUCHER_LIMIT_REACHED'
  | 'PROMOTION_INELIGIBLE'
  | 'FREQUENCY_CAP_EXCEEDED'
  | 'QUIET_HOURS_ACTIVE'
  | 'BRANDING_INVALID'
  | 'SCHEMA_VALIDATION_FAILED'
  | 'WEBHOOK_SIGNATURE_INVALID'
  | 'IDEMPOTENCY_REPLAY';

export class KilatError extends Error {
  readonly code: KilatErrorCode;
  readonly meta: Record<string, unknown>;

  constructor(code: KilatErrorCode, message: string, meta: Record<string, unknown> = {}) {
    super(message);
    this.name = 'KilatError';
    this.code = code;
    this.meta = meta;
  }
}

export class NotSignedInError extends KilatError {
  constructor() {
    super('NOT_SIGNED_IN', 'No authenticated user.');
  }
}

export class InsufficientPermissionError extends KilatError {
  constructor(required: string, meta: Record<string, unknown> = {}) {
    super('INSUFFICIENT_PERMISSION', `Required permission: ${required}`, { required, ...meta });
  }
}

export class TenantNotFoundError extends KilatError {
  constructor(tenantId: string) {
    super('TENANT_NOT_FOUND', `Tenant ${tenantId} not found.`, { tenantId });
  }
}

export class InvalidTransitionError extends KilatError {
  constructor(from: string, to: string, meta: Record<string, unknown> = {}) {
    super('INVALID_TRANSITION', `Cannot transition from ${from} to ${to}.`, { from, to, ...meta });
  }
}

export class AgentCodeInvalidError extends KilatError {
  constructor(code: string) {
    super('AGENT_CODE_INVALID', `Agent code ${code} is invalid or inactive.`, { code });
  }
}

export class WebhookSignatureInvalidError extends KilatError {
  constructor(provider: string) {
    super('WEBHOOK_SIGNATURE_INVALID', `Webhook signature invalid for ${provider}.`, { provider });
  }
}

export class IdempotencyReplayError extends KilatError {
  constructor(eventId: string) {
    super('IDEMPOTENCY_REPLAY', `Event ${eventId} already processed.`, { eventId });
  }
}

export class SchemaValidationFailedError extends KilatError {
  constructor(collection: string, zodIssues: unknown) {
    super('SCHEMA_VALIDATION_FAILED', `Schema validation failed for ${collection}.`, {
      collection,
      zodIssues,
    });
  }
}

export function isKilatError(e: unknown): e is KilatError {
  return e instanceof KilatError;
}
